// src/lib/filemaker-sync/useSyncState.tsx
//
// Demo data for the FileMaker sync admin page + portal panel.
// Replaces the real `fmmirror-mcp` data feed until wp11c ships.
//
// Shape mirrors the eventual backend payload:
//   - 11 layouts, each with its own LayoutSyncStatus
//   - per-anomaly object with operator-facing fields
//   - audit trail of the most recent mirror events
//
// All numbers are deterministic so the page renders identically
// across reloads (helps demo screenshots + CI snapshot tests).

import type { AuditEntry } from "@/lib/demoData";
import {
  type AnomalySeverity,
  type FilemakerLayoutKey,
  type LayoutSyncStatus,
  type FilemakerSyncState,
  type SyncAnomaly,
} from "./types";
import { FILEMAKER_LAYOUTS } from "./types";

// ---------- helpers ----------

function isoMinusMinutes(min: number): string {
  return new Date(Date.now() - min * 60_000).toISOString();
}

function isoMinusHours(h: number): string {
  return new Date(Date.now() - h * 3_600_000).toISOString();
}

// ---------- seed ----------

const ANOMALIES: SyncAnomaly[] = [
  {
    id: "ANM-0001",
    layoutKey: "customers",
    sourcePk: "AccountID=3842",
    kind: "missing-required-field",
    severity: "high",
    detectedAt: isoMinusMinutes(4),
    summary: "Customer 'HT-Audio (Liège)' is missing Country (ISO-2)",
    detail: "Field 'Country' in FileMaker 'Customer' layout is empty.",
    suggestedFix:
      "Open FileMaker > Customers > HT-Audio (Liège) > set Country = BE.",
  },
  {
    id: "ANM-0002",
    layoutKey: "products",
    sourcePk: "SKU=AETHERION-MX2",
    kind: "tier-mismatch",
    severity: "medium",
    detectedAt: isoMinusMinutes(17),
    summary: "Aetherion MX2 wholesale price +8% but Vendor.margin drops below 8%",
    detail:
      "Reported by Agent C during the daily reconciliation pass. " +
      "Vendor-margin floor is 8% per finance policy.",
    suggestedFix:
      "Either reduce the wholesale markup in FileMaker or check the pricing-tier mapping for Aetherion.",
  },
  {
    id: "ANM-0003",
    layoutKey: "invoices",
    sourcePk: "INV-2026-0731",
    kind: "currency-fx-stale",
    severity: "medium",
    detectedAt: isoMinusHours(2),
    summary: "Invoice INV-2026-0731 uses FX rate from 2026-06-15 (>30d old)",
    detail: "EUR-USD rate snapshot is 47 days old at detection time.",
    suggestedFix:
      "Run a manual sync of the Currency layout in FileMaker, then re-issue the invoice's FX column.",
  },
  {
    id: "ANM-0004",
    layoutKey: "repairs",
    sourcePk: "FM-RepairID=117",
    kind: "orphan-fk",
    severity: "high",
    detectedAt: isoMinusHours(5),
    summary: "Repair ticket FM-RepairID=117 references missing customer AccountID=5099",
    detail:
      "Customer AccountID=5099 was hard-deleted from FileMaker; the repair row still references it.",
    suggestedFix:
      "Open FileMaker > Repairs > REP-117, reassign the customer, or hard-delete the orphan repair row.",
  },
  {
    id: "ANM-0005",
    layoutKey: "producers",
    sourcePk: "VendorID=RV-NL",
    kind: "out-of-range",
    severity: "low",
    detectedAt: isoMinusHours(8),
    summary: "R_Volution NL has terms 'Net 365' which is outside the 0/30/60/90 day list",
    detail: "Producer.terms must be one of [Prepay, Net 30, Net 60, Net 90].",
    suggestedFix:
      "Confirm the 365-day terms with R_Volution; if correct, edit the schema-map.toml whitelist before wp11c lands.",
  },
  {
    id: "ANM-0006",
    layoutKey: "quotations",
    sourcePk: "QUO-2026-4412",
    kind: "duplicate-key",
    severity: "low",
    detectedAt: isoMinusHours(14),
    summary: "Quotation QUO-2026-4412 has a duplicate customer-side reference",
    detail:
      "Both 'ReferenceNumber' and 'CustomerPONumber' resolve to the same external ID; not breaking but ambiguous.",
    suggestedFix:
      "Open FileMaker > Quotations > QUO-2026-4412, clear one of the two fields.",
  },
  {
    id: "ANM-0007",
    layoutKey: "customers",
    sourcePk: "AccountID=3842",
    kind: "schema-drift",
    severity: "critical",
    detectedAt: isoMinusHours(18),
    summary: "Customer layout has a new field 'tax_exemption_certificate_url' not in schema-map",
    detail:
      "Detected by the on-prem sync agent's metadata probe. The field is silently dropped from the mirror.",
    suggestedFix:
      "Add 'tax_exemption_certificate_url' to schema-map.toml under the Customer layout. Even ignoring it requires explicit null: 'ignored'.",
  },
];

const AUDIT: AuditEntry[] = [
  {
    id: "AUD-0001",
    action: "mirror-batch",
    actor: "avc-fm-sync-agent",
    ts: isoMinusMinutes(1),
    summary: "Pulled 7 changed rows from FileMaker (4 customers, 2 invoices, 1 producer)",
  },
  {
    id: "AUD-0002",
    action: "anomaly-detected",
    actor: "fmmirror-mcp",
    ts: isoMinusMinutes(4),
    summary: "Customer HT-Audio (Liège) missing Country ISO-2 — anomaly ANM-0001 opened",
  },
  {
    id: "AUD-0003",
    action: "writeback",
    actor: "ben-fm-writeback",
    ts: isoMinusMinutes(7),
    summary: "Pushed ORD-0423 to FileMaker SalesOrder layout (FM RecordID 4412)",
  },
  {
    id: "AUD-0004",
    action: "schema-drift",
    actor: "avc-fm-sync-agent",
    ts: isoMinusHours(18),
    summary: "Customer layout gained 'tax_exemption_certificate_url' field — alert raised",
  },
  {
    id: "AUD-0005",
    action: "mirror-batch",
    actor: "avc-fm-sync-agent",
    ts: isoMinusHours(2),
    summary: "Pulled 12 changed rows (6 repair status transitions, 4 invoices, 2 producers)",
  },
];

// ---------- state derivation ----------

const LAYOUT_HEALTH: Record<FilemakerLayoutKey, { health: LayoutSyncStatus["health"]; totalRows: number; detail?: string }> = {
  customers: { health: "error", totalRows: 142, detail: "1 critical + 1 high anomaly" },
  products: { health: "warning", totalRows: 612, detail: "1 medium anomaly" },
  "sales-orders": { health: "ok", totalRows: 4218 },
  quotations: { health: "warning", totalRows: 1876, detail: "1 low anomaly" },
  invoices: { health: "warning", totalRows: 3891, detail: "1 medium anomaly (FX stale)" },
  "credit-notes": { health: "ok", totalRows: 187 },
  producers: { health: "warning", totalRows: 28, detail: "1 low anomaly (terms whitelist)" },
  banksters: { health: "ok", totalRows: 4 },
  lendings: { health: "ok", totalRows: 2 },
  repairs: { health: "error", totalRows: 358, detail: "1 high anomaly (orphan FK)" },
  reports: { health: "ok", totalRows: 96 },
};

const LAYOUT_LAST_SYNC: Record<FilemakerLayoutKey, { at: string; delta: number }> = {
  customers: { at: isoMinusMinutes(1), delta: 4 },
  products: { at: isoMinusMinutes(2), delta: 0 },
  "sales-orders": { at: isoMinusMinutes(7), delta: 1 },
  quotations: { at: isoMinusMinutes(11), delta: 0 },
  invoices: { at: isoMinusMinutes(2), delta: 2 },
  "credit-notes": { at: isoMinusMinutes(35), delta: 0 },
  producers: { at: isoMinusMinutes(2), delta: 1 },
  banksters: { at: isoMinusHours(6), delta: 0 },
  lendings: { at: isoMinusHours(12), delta: 0 },
  repairs: { at: isoMinusMinutes(2), delta: 6 },
  reports: { at: isoMinusHours(2), delta: 1 },
};

function buildLayoutStatuses(): Record<FilemakerLayoutKey, LayoutSyncStatus> {
  const out = {} as Record<FilemakerLayoutKey, LayoutSyncStatus>;
  for (const cfg of FILEMAKER_LAYOUTS) {
    const meta = LAYOUT_HEALTH[cfg.key];
    const sync = LAYOUT_LAST_SYNC[cfg.key];
    out[cfg.key] = {
      layoutKey: cfg.key,
      lastSyncedAt: sync.at,
      lastRunDelta: sync.delta,
      totalRows: meta.totalRows,
      health: meta.health,
      ...(meta.detail !== undefined ? { healthDetail: meta.detail } : {}),
      openAnomalies: ANOMALIES.filter((a) => a.layoutKey === cfg.key),
    };
  }
  return out;
}

const SEED_STATE: FilemakerSyncState = {
  serverVersion: "19.2.2",
  agentHeartbeatAt: isoMinusMinutes(1),
  queueDepth: 14,
  audit: AUDIT,
  layouts: buildLayoutStatuses(),
};

// ---------- hook (read-only; pure data derivation; no React state needed) ----------

/**
 * Returns the sync state. v1 is a static seed; the eventual
 * backend feed (fmmirror-mcp -> Postgres -> SSE/WS) replaces
 * this with a real-time subscription. The shape stays the
 * same.
 *
 * Note: this is intentionally NOT a useState/useMemo hook so
 * that the seed is callable from non-React contexts (e.g.
 * Vitest unit tests, server-side rendering).
 */
export function useSyncState(): FilemakerSyncState {
  return SEED_STATE;
}

export function getOpenAnomalies(state: FilemakerSyncState): SyncAnomaly[] {
  const out: SyncAnomaly[] = [];
  for (const cfg of FILEMAKER_LAYOUTS) {
    out.push(...state.layouts[cfg.key].openAnomalies);
  }
  return out;
}

export function getAnomalyCounts(
  state: FilemakerSyncState,
): Record<AnomalySeverity, number> {
  const open = getOpenAnomalies(state);
  const counts = { low: 0, medium: 0, high: 0, critical: 0 } as Record<AnomalySeverity, number>;
  for (const a of open) counts[a.severity] += 1;
  return counts;
}

// Test-only helpers (kept private to avoid leaking into the
// public surface).
export function _seedSyncAnomalies(): SyncAnomaly[] {
  return [...ANOMALIES];
}
export function _seedAudit(): AuditEntry[] {
  return [...AUDIT];
}
